import React from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { cn } from '@/lib/utils';
import { PRIMARY_NAV, SECONDARY_NAV } from './navConfig';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { usePrefetchRoute } from '@/lib/query/hooks';
import { useOrgFeatures } from '@/lib/query/hooks/useOrgFeaturesQuery';

export interface NavigationRailProps {
  /** Optional: used only if we want to keep "More" as a sheet trigger (mobile-like). */
  onOpenMore?: () => void;
}

export function NavigationRail({ onOpenMore }: NavigationRailProps) {
  const pathname = usePathname();
  const prefetch = usePrefetchRoute();
  const { data: orgFeatures } = useOrgFeatures();

  const isHrefActive = (href: string) =>
    pathname === href ||
    (href === '/boards' && pathname === '/pipeline') ||
    (href === '/pipeline' && pathname === '/boards');

  return (
    <nav
      aria-label="Navegação principal (tablet)"
      className={cn(
        'flex',
        'flex-col justify-between',
        'w-20 shrink-0',
        'glass border-r border-[var(--color-border-subtle)]'
      )}
    >
      <div className="flex flex-col items-center gap-2 py-4">
        <div className="w-10 h-10 bg-gradient-to-br from-primary-400 to-primary-700 rounded-xl flex items-center justify-center shadow-md shadow-primary-500/25">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <rect x="1" y="7" width="18" height="12" rx="2.5" fill="white" opacity="0.2" />
            <path d="M10 1L19 8H1L10 1Z" fill="white" />
            <rect x="7" y="11" width="6" height="8" rx="1.5" fill="white" />
          </svg>
        </div>
      </div>

      <div className="flex-1 px-3 py-2 overflow-y-auto scrollbar-custom">
        <div className="space-y-2">
          {PRIMARY_NAV.filter((i) => i.id !== 'more' && (i.id !== 'messaging' || (orgFeatures?.messaging_enabled ?? true))).map((item) => {
            const Icon = item.icon;
            const isActive = item.href ? isHrefActive(item.href) : false;

            return (
              <Tooltip key={item.id}>
                <TooltipTrigger asChild>
                  <Link
                    href={item.href!}
                    onMouseEnter={() => prefetch(item.href!)}
                    className={cn(
                      'w-full h-12 rounded flex items-center justify-center transition-colors focus-visible-ring',
                      isActive
                        ? 'bg-primary-500/10 text-primary-600 dark:text-primary-400 border border-primary-200 dark:border-primary-900/50'
                        : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-slate-900 dark:hover:text-white'
                    )}
                    aria-current={isActive ? 'page' : undefined}
                    aria-label={item.label}
                  >
                    <Icon className={cn('h-5 w-5', isActive ? 'text-primary-500' : '')} aria-hidden="true" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {item.label}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        <div className="my-3 h-px bg-slate-200/60 dark:bg-white/10" />

        <div className="space-y-2">
          {SECONDARY_NAV.map((item) => {
            const Icon = item.icon;
            const isActive = isHrefActive(item.href);
            return (
              <Tooltip key={item.id}>
                <TooltipTrigger asChild>
                  <Link
                    href={item.href}
                    onMouseEnter={() => prefetch(item.href)}
                    className={cn(
                      'w-full h-12 rounded flex items-center justify-center transition-colors focus-visible-ring',
                      isActive
                        ? 'bg-primary-500/10 text-primary-600 dark:text-primary-400 border border-primary-200 dark:border-primary-900/50'
                        : 'text-slate-500 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-white/5 hover:text-slate-900 dark:hover:text-white'
                    )}
                    aria-current={isActive ? 'page' : undefined}
                    aria-label={item.label}
                  >
                    <Icon className={cn('h-5 w-5', isActive ? 'text-primary-500' : '')} aria-hidden="true" />
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="right">
                  {item.label}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </div>

      <div className="px-3 pb-4" />
    </nav>
  );
}

